// The env-derived superuser's grants and revocations (permission model 05-administration §3 "Reaches the superuser",
// 1.2.0; the owner's ruling C8 of 2026-10-05): the most powerful grant in the app, read from its own durable table and
// surfaced beside the group history — on the Groups page, where the carriers whose histories it sits beside are listed.
// The Groups page is superuser-only, and so is the route (a 403 renders as the table's permission-denied state). The
// rows name the account, never its address; a deleted account reads as the name the server resolves for it.
import { Link } from 'react-router-dom';
import type { SuperuserAuditEntry, SuperuserAuditResponse } from '../../shared/api-types.js';
import { AUDIT_SURFACE_LABELS, type SuperuserTrailAction } from '../../shared/enums.js';
import { PATHS } from '../paths.js';
import {
  DataTable,
  RelativeTime,
  Section,
  browserTimeZone,
  timeZoneLabel,
  useRequest,
  type DataTableColumn,
  type DataTableStatus,
} from '../ui/index.js';
import { adminApi } from './admin-api.js';

/** The change each row records, in the history's words. */
export const SUPERUSER_CHANGE_LABELS: Readonly<Record<SuperuserTrailAction, string>> = {
  'subject.superuser.grant': 'Granted',
  'subject.superuser.revoke': 'Revoked',
};

export function SuperuserHistory() {
  const trail = useRequest<SuperuserAuditResponse>((signal) => adminApi.superuserAudit(signal), []);
  // 06-modules §3.2: when, absolute and with its time zone — the audit log's own form.
  const timeZone = browserTimeZone();
  const columns: DataTableColumn<SuperuserAuditEntry>[] = [
    {
      key: 'when',
      header: `When (${timeZoneLabel(timeZone)})`,
      name: 'When',
      type: 'date',
      card: 'meta',
      sortValue: (row) => row.createdAt,
      cell: (row) => <RelativeTime value={row.createdAt} mode="absolute" timeZone={timeZone} />,
    },
    { key: 'change', header: 'Change', card: 'field', sortValue: (row) => SUPERUSER_CHANGE_LABELS[row.action], cell: (row) => SUPERUSER_CHANGE_LABELS[row.action] },
    {
      key: 'account',
      header: 'Account',
      card: 'title',
      sortValue: (row) => row.subject.name,
      cell: (row) => (row.subject.id ? <Link to={PATHS.adminUser(row.subject.id)} className="hit-area">{row.subject.name}</Link> : row.subject.name),
    },
    { key: 'actor', header: 'By', card: 'field', sortValue: (row) => row.actor.name, cell: (row) => row.actor.name },
    { key: 'surface', header: 'Surface', card: 'field', sortValue: (row) => AUDIT_SURFACE_LABELS[row.surface], cell: (row) => AUDIT_SURFACE_LABELS[row.surface] },
  ];
  const status: DataTableStatus = trail.status === 'loading'
    ? { kind: 'loading', rowCount: 2 }
    : trail.status === 'error' && trail.error
      ? { kind: 'error', failure: trail.error, onRetry: trail.reload }
      : { kind: 'ready' };

  return (
    <Section
      id="superuser-history"
      title="Superuser history"
      description="Granted by the deployment's configuration, not by a group, and revoked only from the command line. Newest first; kept indefinitely, separate from the audit log. Changes made before this history began appear only where the audit log still held them."
    >
      <DataTable<SuperuserAuditEntry, string>
        caption="Superuser grants and revocations"
        failureNoun="the superuser history"
        columns={columns}
        rows={trail.data?.items ?? []}
        rowKey={(row) => row.id}
        status={status}
        sort={{ defaultSort: { key: 'when', dir: 'desc' }, namespace: 'superuser' }}
        clientSort
        empty={{
          title: 'No superuser changes recorded',
          description: 'A grant is recorded when an address on the deployment\'s superuser list signs in, and a revocation when the command line removes one.',
        }}
      />
    </Section>
  );
}
